import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { motion, AnimatePresence } from 'framer-motion';
import './ProtocolsForm.css';
import { jsPDF } from 'jspdf';
import { AxiosError } from 'axios';

interface PersonalData {
  id_personal_data: number;
  id_user: number;
  name: string;
  sex: string;
  race: string;
  social_name: string;
  marital_status: string;
  date_of_birth: string;
  residential_phone: string;
  cell_phone: string;
  workplace_phone: string;
  current_capacity: string;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

/** ====== CONFIGURAÇÃO DA LOGO (em /public) ====== */
const LOGO_FILE = 'censo g2_page-0001.jpg';

/** carrega imagem do /public como DataURL (para o PDF) */
const loadPublicImageAsDataURL = async (fileName: string) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const url = encodeURI(`${base}/${fileName}`); // encodeURI para lidar com espaços
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Falha ao carregar imagem: ${url}`);

  const blob = await res.blob();
  const dataURL = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const dim = await new Promise<{ w: number; h: number }>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataURL;
  });

  return { dataURL, ...dim };
};

/** monta URL segura de arquivo no /public (para a impressão em nova janela) */
const publicUrl = (fileName: string) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  return encodeURI(`${base}/${fileName}`);
};

const ProtocolsForm: React.FC = () => {
  const { token, id_user, email } = useAuth();
  const navigate = useNavigate();
  const [protocoloGerado, setProtocoloGerado] = useState('');
  const [personalData, setPersonalData] = useState<PersonalData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const gerarToken = () => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10).replace(/-/g, '');
    const randomDigits = Math.floor(10000 + Math.random() * 90000);
    return `PROTO-${date}-${randomDigits}`;
  };

  useEffect(() => {
    const fetchPersonalData = async () => {
      if (!token || !id_user) {
        setErrorMessage('Sessão expirada. Faça login novamente.');
        navigate('/rj');
        return;
      }
      try {
        const response = await api.get(`/personaldata/${id_user}`);
        if (response.data.message === 'Personal data retrieved') {
          setPersonalData(response.data.data);
        } else {
          throw new Error('Resposta inválida ao buscar dados pessoais.');
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao buscar dados pessoais:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Erro ao buscar dados pessoais.');
      }
    };
    fetchPersonalData();
  }, [token, id_user, navigate]);

  useEffect(() => {
    if (!token || !id_user || !email) {
      setErrorMessage('Sessão expirada ou dados incompletos. Faça login novamente.');
      navigate('/rj');
      return;
    }

    const salvarProtocolo = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      const novoToken = gerarToken();
      setProtocoloGerado(novoToken);

      try {
        const now = new Date();
        const date = now.toISOString().split('T')[0];
        const time = now.toLocaleTimeString('pt-BR', { hour12: false });

        await api.post('/protocol', {
          protocol: novoToken,
          date,
          time,
          situation: 'pendente',
        });

        const emailHtml = `
          <h1>Confirmação de Protocolo - Censo Prev</h1>
          <p><strong>Nome:</strong> ${personalData?.name || 'Usuário'}</p>
          <p><strong>Protocolo:</strong> ${novoToken}</p>
          <p><strong>Data:</strong> ${now.toLocaleDateString('pt-BR')}</p>
          <p><strong>Hora:</strong> ${time}</p>
          <p><strong>Status:</strong> Em análise</p>
          <p>Este documento foi gerado automaticamente.</p>
        `;

        await api.post(
          '/email/send',
          {
            to: email,
            subject: 'Confirmação de Protocolo - Censo Prev',
            text: `Protocolo gerado para ${personalData?.name || 'Usuário'}. Protocolo: ${novoToken}, Data: ${now.toLocaleDateString('pt-BR')}, Hora: ${time}, Status: Em análise`,
            html: emailHtml,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );


      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao salvar protocolo ou enviar e-mail:', error.response?.status, error.response?.data);
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Erro ao gerar o protocolo ou enviar o e-mail de confirmação.';
        setErrorMessage(message);
      } finally {
        setIsLoading(false);
      }
    };

    salvarProtocolo();
  }, [token, id_user, email, personalData, navigate]);

  /** ===== PDF com a LOGO no topo ===== */
  const downloadPDF = async () => {
    if (!protocoloGerado || !personalData) return;

    const doc = new jsPDF('p', 'mm', 'a4'); // A4 retrato
    const pageWidth = doc.internal.pageSize.getWidth();
    let yStart = 15;

    try {
      const { dataURL, w, h } = await loadPublicImageAsDataURL(LOGO_FILE);
      const logoWidth = Math.min(60, pageWidth - 40); // largura máxima 60mm
      const logoHeight = (h / w) * logoWidth;
      const x = (pageWidth - logoWidth) / 2;
      const y = 10;

      // Detecta o tipo (PNG/JPEG) pelo dataURL
      const imgType = dataURL.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(dataURL, imgType as any, x, y, logoWidth, logoHeight);
      yStart = y + logoHeight + 10;
    } catch (e) {
      console.warn('Logo não carregada, seguindo sem imagem:', e);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    doc.text('Comprovante de Protocolo', 20, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Nome:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(personalData.name, 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Protocolo:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(protocoloGerado, 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Data:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(new Date().toLocaleDateString('pt-BR'), 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Hora:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text(new Date().toLocaleTimeString('pt-BR', { hour12: false }), 50, yStart);
    yStart += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Status:', 20, yStart);
    doc.setFont('helvetica', 'normal'); doc.text('Em análise', 50, yStart);
    yStart += 12;

    // Rodapé
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Este documento foi gerado automaticamente.', 20, yStart);

    // Moldura
    doc.setDrawColor(0, 51, 102);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, doc.internal.pageSize.getWidth() - 20, doc.internal.pageSize.getHeight() - 20, 'S');

    doc.save(`protocolo_${protocoloGerado}.pdf`);
  };

  /** ===== Impressão com a LOGO no topo ===== */
  const printProtocol = () => {
    if (!protocoloGerado || !personalData) return;

    const logoSrc = publicUrl(LOGO_FILE);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Imprimir Protocolo</title>
            <style>
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
              body { font-family: Arial, sans-serif; padding: 24px; text-align: center; }
              .logo {
                display: block;
                margin: 0 auto 12px auto;
                width: 140px;
                height: auto;
              }
              .protocol-container {
                max-width: 420px; margin: 0 auto; padding: 20px;
                border: 2px solid #003366; border-radius: 8px; background-color: #f9f9f9;
                text-align: left;
              }
              h1 { color: #003366; background-color: #c8dcff; padding: 10px;
                   margin: 12px -20px 16px -20px; border-radius: 6px 6px 0 0; text-align: center; }
              h2 { color: #286ea7; text-align: center; margin: 0 0 12px 0; }
              .row { margin: 8px 0; }
              .protocol { font-weight: bold; }
              .label { font-weight: bold; display: inline-block; width: 110px; }
              .footer { color: #646464; font-size: 12px; margin-top: 16px; text-align: center; }
            </style>
          </head>
          <body>
            <img class="logo" src="${logoSrc}" alt="Logo Censo Prev" />
            <div class="protocol-container">
              <h1>Censo Prev</h1>
              <h2>Comprovante de Protocolo</h2>

              <div class="row"><span class="label">Nome:</span><span>${personalData.name}</span></div>
              <div class="row"><span class="label">Protocolo:</span><span class="protocol">${protocoloGerado}</span></div>
              <div class="row"><span class="label">Data:</span><span>${new Date().toLocaleDateString('pt-BR')}</span></div>
              <div class="row"><span class="label">Hora:</span><span>${new Date().toLocaleTimeString('pt-BR', { hour12: false })}</span></div>
              <div class="row"><span class="label">Status:</span><span>Em análise</span></div>

              <p class="footer">Este documento foi gerado automaticamente.</p>
            </div>

            <script>
              // garante que a imagem carregou antes de imprimir
              const imgs = Array.from(document.images || []);
              Promise.all(
                imgs.map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = img.onerror = res; }))
              ).then(() => {
                window.print();
                window.onafterprint = () => window.close();
              });
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  if (!token || !id_user) {
    return (
      <>
        <TopBar />
        <PageTransition>
          <div className="protocol-wrapper">
            <div className="protocol-box">
              <h2>Sessão expirada</h2>
              <p>Faça login novamente.</p>
              <Button onClick={() => navigate('/rj')} aria-label="Ir para a página de login">
                Ir para Login
              </Button>
            </div>
          </div>
        </PageTransition>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="protocol-wrapper">
          <WizardStepper current={7} />
          <h2>Protocolo enviado para análise</h2>
          <AnimatePresence>
            {errorMessage && <Alert variant="error" className="protocol-alert">{errorMessage}</Alert>}
          </AnimatePresence>
          {protocoloGerado && personalData && (
            <motion.div
              className="protocol-box"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            >
              <div className="protocol-check" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12l5 5L20 6" />
                </svg>
              </div>
              <p>Seu protocolo foi gerado com sucesso.</p>
              <p><strong>Nome:</strong> {personalData.name}</p>
              <p className="protocol-number">{protocoloGerado}</p>
              <p>O documento será analisado pela equipe responsável.</p>
              <div className="protocol-actions">
                <Button
                  onClick={() => { void downloadPDF(); }}
                  disabled={isLoading}
                  aria-label="Baixar comprovante de protocolo em PDF"
                >
                  Baixar PDF
                </Button>
                <Button
                  variant="ghost"
                  onClick={printProtocol}
                  disabled={isLoading}
                  aria-label="Imprimir comprovante de protocolo"
                >
                  Imprimir Protocolo
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/rj')}
                  disabled={isLoading}
                  aria-label="Voltar para a página de login"
                >
                  Voltar para Login
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </PageTransition>
    </>
  );
};

export default ProtocolsForm;
